import { fetchConfig } from '@theme/utilities';

/**
 * <buy-now-button> wraps a plain button inside the product form. On click it adds the
 * currently selected variant/quantity to the cart, then redirects straight to the
 * standard Shopify checkout — skipping the cart page and any accelerated/branded
 * checkout flow, so the customer always sees the normal checkout with every payment
 * method that's enabled for the store.
 */
class BuyNowButton extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    this.addEventListener('click', this.#handleClick, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** @param {MouseEvent} event */
  #handleClick = async (event) => {
    event.preventDefault();

    const button = this.querySelector('button');
    const form = this.closest('form');

    if (!(button instanceof HTMLButtonElement) || !(form instanceof HTMLFormElement) || button.disabled) return;

    this.#setError('');
    button.disabled = true;

    const formData = new FormData(form);

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('javascript', { body: formData }));
      const result = await response.json();

      if (result.status) {
        throw new Error(result.description || result.message || 'Unable to add this item to your cart.');
      }

      window.location.href = '/checkout';
    } catch (error) {
      button.disabled = false;
      this.#setError(error instanceof Error ? error.message : 'Something went wrong. Please try again.');
    }
  };

  /** @param {string} message */
  #setError(message) {
    let errorEl = this.querySelector('.buy-now-button__error');

    if (!message) {
      errorEl?.classList.add('hidden');
      return;
    }

    if (!errorEl) {
      errorEl = document.createElement('span');
      errorEl.className = 'buy-now-button__error';
      this.append(errorEl);
    }

    errorEl.textContent = message;
    errorEl.classList.remove('hidden');
  }
}

if (!customElements.get('buy-now-button')) {
  customElements.define('buy-now-button', BuyNowButton);
}
